import type { ComponentType, ReactNode } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { cn } from '@systemdox/utils';

/** In-SPA link renderer the app injects (react-router `Link`), so the package
 *  stays router-free. Given a string path; the app may add the query string. */
export type LinkComponent = ComponentType<{
  to: string;
  className?: string;
  title?: string;
  children: ReactNode;
}>;

/**
 * One destination card — an icon chip, a title, a one-line description, and a
 * trailing affordance (an in-app arrow, or an external glyph when the target
 * opens on another host). The card navigates one of three ways, in priority:
 * `to` (in-SPA, via the injected `link`), `href` (external `<a>`), else a
 * non-navigating disabled tile. Presentational only.
 */
export interface DocCardItem {
  key: string;
  icon: ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  /** In-SPA target (rendered via the injected `link`). */
  to?: string;
  /** External target (opens in a new tab). */
  href?: string;
  /** Non-navigating tile (e.g. the console URL can't be resolved yet). */
  disabled?: boolean;
  /** Hover title for the whole card. */
  cardTitle?: string;
}

const CARD_BASE =
  'group flex items-start gap-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 transition-all';

function CardBody({ item }: { item: DocCardItem }) {
  const { icon: Icon, iconBg, iconColor, title, description, href } = item;
  const external = !!href;
  const Glyph = external ? ExternalLink : ArrowRight;
  return (
    <>
      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', iconBg)}>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
          {title}
          <Glyph className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
      </div>
    </>
  );
}

export function DocCard({ item, link: Link }: { item: DocCardItem; link?: LinkComponent }) {
  const body = <CardBody item={item} />;

  if (item.disabled) {
    return (
      <div aria-disabled="true" title={item.cardTitle} className={cn(CARD_BASE, 'opacity-60 cursor-not-allowed')}>
        {body}
      </div>
    );
  }
  if (item.to && Link) {
    return (
      <Link to={item.to} title={item.cardTitle} className={cn(CARD_BASE, 'hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md')}>
        {body}
      </Link>
    );
  }
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" title={item.cardTitle}
        className={cn(CARD_BASE, 'hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md')}>
        {body}
      </a>
    );
  }
  // A `to` item with no injected link is a caller wiring bug — render inert
  // rather than a dead <a> that would full-page-reload an in-SPA route.
  return (
    <div aria-disabled="true" title={item.cardTitle} className={cn(CARD_BASE, 'opacity-60 cursor-not-allowed')}>
      {body}
    </div>
  );
}
